'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { geistMonoFont } from '@/_style/fonts';
import Image from 'next/image';
import { useSession } from '@/lib/auth-client';
import { useConversationsQuery } from '@/_state/queries';
import { useDeleteConversation } from '@/_state/mutations';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { IConversation } from '@/types';
import { MessageCircle, PlusCircle, Trash2, ChevronLeft, ChevronRight, Compass } from 'lucide-react';
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarFooter,
	useSidebar
} from '../UIComponents/Sidebar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../UIComponents/DropdownMenu';
import { Button } from '../UIComponents/Button';
import { Skeleton } from '../UIComponents/Skeleton';
import DeleteConversationDialog from '../DeleteConversationDialog/DeleteConversationDialog';
import { DEFAULT_CONVERSATION_PAGE_SIZE } from '@/_constants/defaultConversationPageSize';
import BetaBadge from '../BetaBadge/BetaBadge';

function SingleConvo({
	conversation,
	onDelete,
	isDeleting,
	isActive
}: {
	conversation: IConversation;
	onDelete: (id: string) => void;
	isDeleting: boolean;
	isActive: boolean;
}) {
	return (
		<div className='group relative flex items-center'>
			<Link
				key={conversation.id}
				href={`/conversation/${conversation.id}`}
				className={`text-foreground hover:bg-accent block flex-1 cursor-pointer overflow-hidden rounded-md p-2 pr-8 text-sm text-ellipsis whitespace-nowrap transition-colors ${
					isActive ? 'bg-accent font-medium' : ''
				}`}
			>
				<div className='w-full overflow-hidden text-ellipsis'>{conversation.title}</div>
			</Link>
			<DeleteConversationDialog onDelete={() => onDelete(conversation.id)}>
				<button
					type='button'
					className='text-muted-foreground hover:bg-accent absolute right-1 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400'
					onClick={(e) => e.stopPropagation()}
					disabled={isDeleting}
					aria-label='Delete conversation'
				>
					<Trash2 className='h-4 w-4' />
				</button>
			</DeleteConversationDialog>
		</div>
	);
}

/**
 * Skeleton loading state for the sidebar conversation list
 */
function SidebarConversationsSkeleton() {
	// Varying widths for more realistic appearance
	const skeletonWidths = [
		'w-full',
		'w-4/5',
		'w-3/4',
		'w-5/6',
		'w-2/3',
		'w-2/3',
		'w-5/6',
		'w-4/5',
		'w-3/4',
		'w-5/6',
		'w-4/5',
		'w-3/4',
		'w-5/6'
	];

	return (
		<>
			{skeletonWidths.map((width, index) => (
				<SidebarMenuItem key={index}>
					<div className='flex items-center gap-2 rounded-md p-2'>
						<Skeleton className='h-4 w-4 flex-shrink-0 rounded' />
						<Skeleton className={`h-4 ${width}`} />
					</div>
				</SidebarMenuItem>
			))}
		</>
	);
}

/**
 * Compact pagination controls for the sidebar
 */
function SidebarPagination({
	page,
	totalPages,
	onPageChange,
	disabled
}: {
	page: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	disabled?: boolean;
}) {
	if (totalPages <= 1) return null;

	return (
		<div className='flex items-center justify-between px-2 py-2'>
			<Button
				className='cursor-pointer'
				variant='ghost'
				size='icon-sm'
				onClick={() => onPageChange(page - 1)}
				disabled={disabled || page <= 1}
				aria-label='Previous page'
			>
				<ChevronLeft className='h-4 w-4' />
			</Button>
			<span className='text-muted-foreground cursor-default text-xs select-none'>
				{page} / {totalPages}
			</span>
			<Button
				className='cursor-pointer'
				variant='ghost'
				size='icon-sm'
				onClick={() => onPageChange(page + 1)}
				disabled={disabled || page >= totalPages}
				aria-label='Next page'
			>
				<ChevronRight className='h-4 w-4' />
			</Button>
		</div>
	);
}

function AppSidebar() {
	const [page, setPage] = useState(1);
	const pathname = usePathname();
	const { data: session, isPending: isSessionPending } = useSession();

	const { data: paginatedData, isFetching } = useConversationsQuery(page, DEFAULT_CONVERSATION_PAGE_SIZE);

	// Extract data from paginated response
	const conversations = paginatedData?.conversations ?? [];
	const totalPages = paginatedData?.totalPages ?? 1;
	const total = paginatedData?.total ?? 0;

	// Handle page adjustment when current page becomes empty after deletion
	const handlePageAdjust = useCallback(() => {
		// After deletion, if current page is empty and we're not on page 1, go to previous page
		if (page > 1 && conversations.length === 1) {
			setPage((prev) => Math.max(1, prev - 1));
		}
	}, [page, conversations.length]);

	const { mutate: deleteConversation, isPending: isDeleting } = useDeleteConversation(handlePageAdjust);

	const { open } = useSidebar();

	// Show skeleton only while session is resolving or while authenticated user's data is loading.
	// Unauthenticated users skip straight to the empty state.
	const showSkeleton = isSessionPending || (!!session?.user && !paginatedData);

	// Ensure page is valid after data changes (e.g., if total pages decreased)
	useEffect(() => {
		if (paginatedData && page > totalPages && totalPages > 0) {
			setPage(totalPages);
		}
	}, [paginatedData, page, totalPages]);

	const handleDelete = (id: string) => {
		deleteConversation(id);
	};

	const handlePageChange = (newPage: number) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setPage(newPage);
		}
	};

	return (
		<Sidebar
			collapsible='icon'
			variant='inset'
		>
			<SidebarHeader>
				<div className='flex flex-col gap-y-2'>
					<Link
						href='/'
						className='text-foreground flex items-center gap-2'
					>
						<Image
							src='/logo.svg'
							alt='Blockpedia Logo'
							width={32}
							height={32}
							style={{ filter: 'var(--logo-filter)' }}
						/>
						{open && (
							<>
								<h1 className={`${geistMonoFont.className} text-lg font-medium`}>blockpedia</h1>
								<BetaBadge />
							</>
						)}
					</Link>
					<Link
						href='/new'
						className='w-full'
					>
						<Button className='flex w-full cursor-pointer items-center gap-x-1'>
							<PlusCircle />
							{open && 'New Conversation'}
						</Button>
					</Link>

					<Link
						href='/discover'
						className='w-full'
					>
						<Button
							variant='secondary'
							className='mt-1 flex w-full cursor-pointer items-center gap-x-1'
						>
							<Compass />
							{open && 'Discover'}
						</Button>
					</Link>
				</div>
			</SidebarHeader>
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupLabel>Conversations{total > 0 && open ? ` (${total})` : ''}:</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{open ? (
								showSkeleton ? (
									<SidebarConversationsSkeleton />
								) : conversations.length === 0 ? (
									<SidebarMenuItem>
										<div className='text-muted-foreground px-2 py-3 text-sm'>
											No conversations yet. Start a new one!
										</div>
									</SidebarMenuItem>
								) : (
									conversations.map((conversation) => (
										<SidebarMenuItem key={conversation.id}>
											<SidebarMenuButton asChild>
												<SingleConvo
													conversation={conversation}
													onDelete={handleDelete}
													isDeleting={isDeleting}
													isActive={pathname === `/conversation/${conversation.id}`}
												/>
											</SidebarMenuButton>
										</SidebarMenuItem>
									))
								)
							) : (
								<SidebarMenuItem>
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant='secondary'
												size='icon'
											>
												<MessageCircle />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent
											align='start'
											side='right'
											sideOffset={10}
											className='h-96 w-52'
										>
											{showSkeleton ? (
												<div className='flex flex-col gap-2 p-2'>
													{[1, 2, 3, 4, 5].map((i) => (
														<Skeleton
															key={i}
															className='h-8 w-full'
														/>
													))}
												</div>
											) : (
												conversations.map((conversation) => (
													<DropdownMenuItem key={conversation.id}>
														<SingleConvo
															conversation={conversation}
															onDelete={handleDelete}
															isDeleting={isDeleting}
															isActive={pathname === `/conversation/${conversation.id}`}
														/>
													</DropdownMenuItem>
												))
											)}
										</DropdownMenuContent>
									</DropdownMenu>
								</SidebarMenuItem>
							)}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			{open && totalPages > 1 && (
				<SidebarFooter>
					<SidebarPagination
						page={page}
						totalPages={totalPages}
						onPageChange={handlePageChange}
						disabled={isFetching}
					/>
				</SidebarFooter>
			)}
		</Sidebar>
	);
}

export default AppSidebar;
